import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormLabel from '@material-ui/core/FormLabel';
import WarningIcon from '@material-ui/icons/Warning';
import { surgeryService } from '../../../services/surgeryService';

const CONVERSION_REASONS = [
  'Post-operative complication',
  'Unexpected findings',
  'Patient preference',
  'Anaesthesia-related',
  'Uncontrolled pain',
  'Bleeding/haemorrhage',
  'Other',
];

const WARD_OPTIONS = [
  'Surgical Ward A',
  'Surgical Ward B',
  'ICU',
];

const useStyles = makeStyles(() => ({
  warningBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '12px 16px',
    backgroundColor: '#FFF3E0',
    borderRadius: 8,
    border: '1px solid #FFB74D',
    marginBottom: 20,
  },
  warningIcon: { color: '#E65100', marginTop: 2 },
  warningText: { fontSize: '0.88rem', color: '#BF360C', lineHeight: 1.5 },
  formControl: { marginBottom: 20, width: '100%' },
  confirmBtn: {
    backgroundColor: '#D32F2F',
    color: '#fff',
    fontWeight: 600,
    '&:hover': { backgroundColor: '#B71C1C' },
  },
  title: { fontWeight: 700 },
  urgencyLabel: { fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 },
}));

const ConvertToInpatientDialog = ({ open, onClose, onConfirm, surgeryId }) => {
  const classes = useStyles();
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [ward, setWard] = useState('');
  const [urgency, setUrgency] = useState('Routine');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    const payload = { reason, notes, ward, urgency, surgeryId };
    setSubmitting(true);
    try {
      await surgeryService.convertToInpatient(surgeryId, payload);
    } catch (err) {
      // API not yet wired — continue with mock flow
    }
    setSubmitting(false);
    onConfirm(payload);
  };

  const canConfirm = reason && ward;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography className={classes.title}>Convert to Inpatient Admission</Typography>
      </DialogTitle>

      <DialogContent>
        {/* Warning Banner */}
        <div className={classes.warningBanner}>
          <WarningIcon className={classes.warningIcon} />
          <Typography className={classes.warningText}>
            This will convert the day-case surgery to an inpatient pathway requiring admission.
          </Typography>
        </div>

        {/* Reason for Conversion */}
        <FormControl variant="outlined" size="small" className={classes.formControl}>
          <Label>Reason for Conversion</Label>
          <Select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            label="Reason for Conversion"
          >
            {CONVERSION_REASONS.map((r) => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Additional Notes */}
        <TextField
          className={classes.formControl}
          label="Additional Notes"
          variant="outlined"
          size="small"
          multiline
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
        />

        {/* Ward Preference */}
        <FormControl variant="outlined" size="small" className={classes.formControl}>
          <Label>Ward Preference</Label>
          <Select
            value={ward}
            onChange={(e) => setWard(e.target.value)}
            label="Ward Preference"
          >
            {WARD_OPTIONS.map((w) => (
              <MenuItem key={w} value={w}>{w}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Urgency */}
        <FormControl component="fieldset" className={classes.formControl}>
          <FormLabel className={classes.urgencyLabel}>Urgency</FormLabel>
          <RadioGroup
            row
            value={urgency}
            onChange={(e) => setUrgency(e.target.value)}
          >
            <FormControlLabel value="Routine" control={<Radio color="primary" />} label="Routine" />
            <FormControlLabel value="Urgent" control={<Radio color="secondary" />} label="Urgent" />
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions style={{ padding: '8px 24px 16px' }}>
        <Button onClick={onClose} variant="outlined" disabled={submitting}>
          Cancel
        </Button>
        <Button
          className={classes.confirmBtn}
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm || submitting}
        >
          {submitting ? 'Converting...' : 'Confirm Conversion'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConvertToInpatientDialog;
